import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, CheckCircle, XCircle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const HERO_IMG = "https://images.unsplash.com/photo-1696846311707-792070142094?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxpdnklMjBsZWFndWUlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXV0dW1ufGVufDB8fHx8MTc3Mzg4Nzc1M3ww&ixlib=rb-4.1.0&q=85";

const questions = [
  {
    question: "Will multiple experienced advisors review my child's application?",
    us: "Every application is reviewed by multiple former admissions interviewers and senior advisors.",
    them: "Most firms assign a single counselor, often part-time or junior.",
  },
  {
    question: "Is strategy built early or only applied at essay time?",
    us: "Strategic positioning begins early — course planning, summer enrichment, narrative development.",
    them: "Many firms only engage during senior year for essay editing.",
  },
  {
    question: "Is there structural analysis behind the feedback?",
    us: "Layered analysis focused on structure, coherence, institutional fit, and committee dynamics.",
    them: "Surface-level feedback focused on grammar and cosmetic polish.",
  },
  {
    question: "Does the firm understand how admissions committees deliberate?",
    us: "Our team includes former Harvard admissions interviewers who've sat in committee rooms.",
    them: "Most counselors have never participated in an admissions committee.",
  },
  {
    question: "Is the process proactive or reactive?",
    us: "Proactive framework building from the start — every decision reinforces the application thesis.",
    them: "Reactive editing and last-minute scrambling.",
  },
];

export default function ComparePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="compare-page">
      <SEO
        title="Why Us — How We Compare to Typical Admissions Firms"
        description="Multi-advisor review vs. single junior counselor. Early strategic positioning vs. senior-year essay editing. Former admissions interviewers vs. counselors who have never sat in committee. See why families choose Ivy League Consulting."
      />
      {/* Nav */}
      <MarketingNav />

      {/* Hero with Image */}
      <section className="pt-16 relative overflow-hidden">
        <div className="relative h-[50vh] min-h-[380px] max-h-[520px]">
          <img src={HERO_IMG} alt="University campus" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B] via-[#0F392B]/75 to-[#0F392B]/35" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-6">Choosing the Right Partner</p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Choosing the Right<br /><span className="italic">Admissions Partner</span>
              </h1>
              <p className="text-base md:text-lg text-white/75 max-w-xl mx-auto">
                Families should ask the right questions. Here's how we answer them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {questions.map((q, i) => (
            <div key={i} className="rounded-xl border border-[#E2E2E2] overflow-hidden hover:shadow-md transition-shadow" data-testid={`compare-item-${i}`}>
              <div className="p-6 bg-[#F9F9F7] border-b border-[#E2E2E2]">
                <p className="font-medium text-base" style={{ fontFamily: "'Playfair Display', serif" }}>"{q.question}"</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E2E2E2]">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-[#0F392B]" strokeWidth={2} />
                    <span className="text-xs font-medium text-[#0F392B] uppercase tracking-wide">Ivy League Consulting</span>
                  </div>
                  <p className="text-sm leading-relaxed">{q.us}</p>
                </div>
                <div className="p-6 bg-[#FAFAFA]">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="h-4 w-4 text-muted-foreground/50" strokeWidth={2} />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Typical Firms</span>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{q.them}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 bg-[#F9F9F7] border-y border-[#E2E2E2]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Quote className="h-8 w-8 text-[#D4AF37]/30 mx-auto mb-4" strokeWidth={1} />
          <p className="text-lg text-muted-foreground mb-2">We are not a content factory.</p>
          <p className="text-2xl md:text-3xl font-medium text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
            We are a <span className="italic">strategy firm.</span>
          </p>
        </div>
      </section>

      {/* CTA with Image */}
      <section className="relative h-[40vh] min-h-[320px]">
        <img src="https://images.unsplash.com/photo-1686467287619-b0300f8b75ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxoYXJ2YXJkJTIweWFsZSUyMHVuaXZlcnNpdHklMjBidWlsZGluZyUyMGNsYXNzaWMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzczODg3NzU1fDA&ixlib=rb-4.1.0&q=85" alt="University building" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F392B]/85" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              The bridge is real. The work is rigorous.
            </h2>
            <p className="text-base text-white/70 mb-8">The outcomes follow.</p>
            <Link to="/sign-up">
              <Button size="lg" className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base shadow-xl" data-testid="compare-cta-btn">
                Build My Strategy <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
            <span className="font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/case-method" className="hover:text-foreground transition-colors">The Case Method</Link>
            <Link to="/portal" className="hover:text-foreground transition-colors">$99 Portal</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Packages</Link>
          </div>
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Ivy League Consulting. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
